import {
  faArrowRightLong,
  faArrowUpFromArc,
} from '@fortawesome/pro-regular-svg-icons'
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons'
import { Box, Skeleton, Text, Tooltip } from '@mantine/core'
import { useState } from 'react'

import {
  useMutationUpdateBooking,
  useQueryGetAllBookingProducts,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import {
  type CoreBookingProduct,
  type ParameterID,
  type CoreInsuranceQuoteResponseList,
} from '@flecto/types'
import { CardList, Icon, Modal, TextCurrency } from '@flecto/ui'

import { SectionCustomerNotificationEmail } from '../../../booking-customer'
import {
  useBookingInsuranceInfo,
  useInsuranceStatus,
} from '../../../booking-insurances'
import { ButtonBookingFlectoLinkCopy } from '../../../bookings'
import { AlertBookingHasInsuredProducts } from '../AlertBookingHasInsuredProducts/AlertBookingHasInsuredProducts'

export type ModalBookingPickupConfirmationProps = {
  bookingId?: ParameterID
  bookingProductsToPickup?: CoreBookingProduct[]
  onSuccess?: () => void
  quotes?: CoreInsuranceQuoteResponseList
  isLoadingQuotes?: boolean
}

export function ModalBookingPickupConfirmation({
  bookingId,
  bookingProductsToPickup,
  onSuccess,
  quotes,
  isLoadingQuotes,
}: ModalBookingPickupConfirmationProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_pickup_return' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })

  const [notifyCustomer, setNotifyCustomer] = useState(false)

  const { isAccountInsuranceEnabled, isBookingCoveredByInsurance } =
    useBookingInsuranceInfo({ bookingId })

  const { data: booking } = useQueryGetBookingById({ bookingId })
  const { data: bookingItems } = useQueryGetAllBookingProducts({ bookingId })

  const { mutateAsync: updateBooking, isLoading: isUpdating } =
    useMutationUpdateBooking()

  const { mutate } = useInsuranceStatus({ bookingId })

  const totalItemsToPickup = bookingProductsToPickup?.reduce(
    (acc, bi) => acc + Number(bi.local_new_picked_up_quantity),
    0
  )

  const handleSubmit = async () => {
    if (!bookingId) {
      return
    }
    const bookingItemsToUpdate: Pick<
      CoreBookingProduct,
      'id' | 'picked_up_quantity'
    >[] = []

    for (const item of bookingItems ?? []) {
      const pickupBookingItem = bookingProductsToPickup?.find(
        (bi) => bi.id === item.id
      )

      if ((pickupBookingItem?.local_new_picked_up_quantity ?? 0) > 0) {
        bookingItemsToUpdate.push({
          id: item.id,
          picked_up_quantity: pickupBookingItem
            ? (pickupBookingItem?.local_new_picked_up_quantity ?? 0)
            : 0,
        })
      }
    }

    await updateBooking({
      bookingId,
      options: {
        send_email: notifyCustomer,
      },
      booking: {
        booking_items_attributes: bookingItemsToUpdate,
      },
    })

    mutate()

    onSuccess?.()
  }

  return (
    <Modal size='md'>
      <Modal.Icon icon={faArrowUpFromArc} color='orange' />
      <Modal.Title>{t('modal_pickup_confirmation_title')}</Modal.Title>
      <Modal.Content>
        <AlertBookingHasInsuredProducts
          mb='md'
          bookingId={bookingId}
          bookingProductsToPickup={bookingProductsToPickup}
        />

        <CardList mb='md'>
          <CardList.Item>
            <CardList.ItemLabel w={220}>
              {t('modal_pickup_confirmation_items_to_pickup')}
            </CardList.ItemLabel>
            <CardList.ItemValue>
              <Text c='dimmed'>{booking?.meta?.total_picked_up_quantity}</Text>
              <Icon icon={faArrowRightLong} />
              <Text>{totalItemsToPickup}</Text>
            </CardList.ItemValue>
          </CardList.Item>

          {isAccountInsuranceEnabled && isBookingCoveredByInsurance && (
            <CardList.Item>
              <CardList.ItemLabel w={220}>
                {t('modal_pickup_confirmation_insurance_estimated_cost')}
                <Tooltip
                  multiline
                  mx='md'
                  label={t(
                    'modal_pickup_confirmation_insurance_estimated_cost_description'
                  )}
                >
                  <Box display='inline' ml='xs'>
                    <Icon icon={faQuestionCircle} size='sm' />
                  </Box>
                </Tooltip>
              </CardList.ItemLabel>
              <CardList.ItemValue>
                {isLoadingQuotes ? (
                  <Skeleton h={10} w={50} />
                ) : (
                  <TextCurrency cents={quotes?.quote_total_cents ?? 0} lh={1} />
                )}
              </CardList.ItemValue>
            </CardList.Item>
          )}
        </CardList>

        <SectionCustomerNotificationEmail
          mt='lg'
          customerId={booking?.customer?.internal_id}
          value={notifyCustomer}
          onChange={setNotifyCustomer}
        />
      </Modal.Content>
      <Modal.Close />
      <Modal.Cancel />
      <Modal.Submit onClick={handleSubmit} loading={isUpdating}>
        {tActions('confirm')}
      </Modal.Submit>
      <Modal.FooterExtra>
        <ButtonBookingFlectoLinkCopy booking={booking} />
      </Modal.FooterExtra>
    </Modal>
  )
}
